import { Component, h, Element, Method, forceUpdate } from '@stencil/core';

@Component({
  tag: 'dc-ui-header-base',
  styleUrl: 'dc-ui-header-base.scss',
  shadow: true,
})
/**
 * Base header component that provides a flexible layout with optional slots for logo, meta navigation, search, and burger menu.
 * @slot logo - Container for the logo content
 * @slot meta-nav - Container for meta navigation elements
 * @slot search - Container for search functionality
 * @slot burger - Container for burger menu
 */
export class DcUiHeaderBase {
  @Element() el: HTMLElement;

  private metaNavEl?: HTMLElement;
  private searchEl?: HTMLElement;
  private observer: MutationObserver;

  componentDidLoad() {
    // Listen for the custom event
    this.el.addEventListener('languageButtonMoved', this.handleLanguageButtonMoved);

    // Initialize MutationObserver
    this.observer = new MutationObserver(this.handleMutations);

    // Start observing the element for child changes
    this.observer.observe(this.el, {
      childList: true, // Watch for changes to child elements
    });
  }

  disconnectedCallback() {
    // Clean up listener and observer
    this.el.removeEventListener('languageButtonMoved', this.handleLanguageButtonMoved);
    this.observer.disconnect();
  }

  private handleLanguageButtonMoved = () => {
    forceUpdate(this.el);
  };

  private handleMutations = () => {
    forceUpdate(this.el);
  };

  @Method()
  async returnSearchExpandSize(): Promise<number> {
    try {
      if (!this.metaNavEl) {
        // Use percentage-based fallback
        return this.el.getBoundingClientRect().width * 0.5;
      }

      const gapBetweenMetaNavAndSearch = 48;
      const headerWidth = this.el.getBoundingClientRect().width;
      const metaNavWidth = this.metaNavEl.getBoundingClientRect().width;
      const searchWidth = this.searchEl.getBoundingClientRect().width;
      const width = metaNavWidth + searchWidth + gapBetweenMetaNavAndSearch;

      return Math.max(width, headerWidth * 0.5);
    } catch (error) {
      return this.el.getBoundingClientRect().width * 0.5;
    }
  }

  render() {
    // Check if slots have content
    const hasLogo = this.el.querySelector('[slot="logo"]');
    const hasMetaNav = this.el.querySelector('[slot="meta-nav"]');
    const hasSearch = this.el.querySelector('[slot="search"]');
    const hasBurger = this.el.querySelector('[slot="burger"]');
    // Build class string based on slot usage
    const classes = {
      'header-base-wrapper': true,
      'has-logo': !!hasLogo,
      'has-meta-nav': !!hasMetaNav,
      'has-search': !!hasSearch,
    };

    const burger = hasBurger ? (
      <div class="burger-wrapper">
        <slot name="burger" />
      </div>
    ) : null;

    const logo = hasLogo ? (
      <div class="logo-wrapper">
        <slot name="logo" />
      </div>
    ) : null;

    const metaNav = hasMetaNav ? (
      <div
        class="meta-nav-wrapper"
        ref={(el) => (this.metaNavEl = el)}>
        <slot name="meta-nav" />
      </div>
    ) : null;

    const search = hasSearch ? (
      <div
        class="search-wrapper"
        ref={(el) => (this.searchEl = el)}>
        <slot name="search" />
      </div>
    ) : null;

    return (
      <div class={classes}>
        {burger}
        {logo}
        {metaNav}
        {search}
      </div>
    );
  }
}
